import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroCarousel from './components/HeroCarousel';
import CategoryNav from './components/CategoryNav';

function SectionHeader({ title, subtitle, href }: { title: string; subtitle?: string; href?: string }) {
  return (
    <div className="flex items-end justify-between mb-5 md:mb-6">
      <div>
        <h3 className="text-lg md:text-xl font-bold text-foreground-100">{title}</h3>
        {subtitle && <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>}
      </div>
      {href && (
        <Link
          to={href}
          className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-400 transition-colors whitespace-nowrap"
        >
          查看更多
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
        </Link>
      )}
    </div>
  );
}

export default function Home() {
  const hotMovies: MovieItem[] = movies.filter((m) => m.isHot);
  const newMovies: MovieItem[] = movies.filter((m) => m.isNew);
  const tvShows: MovieItem[] = movies.filter((m) => m.category === '电视剧' || m.category === '综艺');
  const recommendedMovies: MovieItem[] = movies.slice(0, 8);

  const homeStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '鲁鲁影院',
    url: 'https://lulucinema.com',
    description: '鲁鲁影院提供高清画质免费在线影视，无需缓冲，海量电影、电视剧、综艺高清流畅播放',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://lulucinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEOHead
        title="鲁鲁影院-高清免费在线不缓冲，免费看最新电影与热播电视剧，高清流畅追剧"
        description="鲁鲁影院提供高清画质免费在线影视，无需缓冲，海量电影、电视剧、综艺高清流畅播放，每日更新热播内容，畅享追剧体验。鲁鲁影院致力于为您打造极致的免费观影平台。"
        keywords="鲁鲁影院,高清免费,在线不缓冲,免费看大片,高清流畅,免费影视,热播剧,电影大全,在线观看,高清追剧"
        canonicalUrl="https://lulucinema.com/"
        structuredData={homeStructuredData}
      />

      <div className="min-h-screen bg-background-100">
        <Navbar />

        <main>
          <HeroCarousel />
          <CategoryNav />

          <section className="py-8 md:py-10">
            <div className="container-main">
              <SectionHeader title="热播推荐" subtitle="大家都在看的精彩内容" href="/category/all" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-10 bg-background-50">
            <div className="container-main">
              <SectionHeader title="最新上线" subtitle="新鲜出炉，第一时间观看" href="/category/all" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {newMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-10">
            <div className="container-main">
              <SectionHeader title="精选好片" subtitle="评分最高，口碑最佳" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {recommendedMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-8 md:py-10 bg-background-50">
            <div className="container-main">
              <SectionHeader title="热播剧集" subtitle="追剧党必备，每日更新" href="/category/tv" />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
                {tvShows.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="container-main">
              <div className="bg-background-200/50 rounded-2xl p-8 md:p-12 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-foreground-100 mb-3">
                  高清免费在线不缓冲
                </h3>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto mb-6 leading-relaxed">
                  鲁鲁影院致力于为广大影迷提供最优质的在线观影体验。海量正版影视资源，涵盖电影、电视剧、综艺、动漫等多种类型。高清画质、流畅播放、完全免费，让您随时随地畅享追剧乐趣。
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/category/all"
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-950 font-semibold text-sm rounded-full transition-all duration-300 whitespace-nowrap"
                  >
                    浏览全部影片
                  </Link>
                  <Link
                    to="/about"
                    className="px-6 py-2.5 bg-background-300/40 hover:bg-background-300/60 text-foreground-200 text-sm rounded-full transition-all duration-300 whitespace-nowrap"
                  >
                    了解更多
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}